/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { FC } from 'react';
import APIError from '../../../errors/api-error';
import LockUnauthorizedIcon from '../../../icons/icons-revamp/lock_unauthorized';
import { TEXT_COLOR_LIGHT } from '../../../typings';
import QSButton from '../../atoms/button';
import QSTypography from '../../atoms/typography';
import OrgOwnersList from '../../../../modules/app/routes/auth-routes/org-owners-list';

interface ErrorContainerProps {
  error?: string;
  errorObject?: APIError;
  unauthorizedTitle?: string;
  featureName?: string;
  showOrgOwners?: boolean;
}

const UNAUTHORIZED_STATUS_CODES = [401, 403];

const ErrorContainer: FC<ErrorContainerProps> = ({
  error = 'Something went wrong',
  errorObject,
  unauthorizedTitle,
  featureName,
  showOrgOwners = true,
}) => {
  const isUnauthorized = !!errorObject && UNAUTHORIZED_STATUS_CODES.includes(errorObject.statusCode);

  if (isUnauthorized) {
    return (
      <div
        css={css`
          height: 100%;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: safe center;
          flex-direction: column;
          row-gap: 24px;
          padding: 48px 24px;
          overflow-y: auto;
        `}
      >
        <div
          css={css`
            width: 56px;
            height: 56px;
            flex-shrink: 0;
            border-radius: 50%;
            background-color: #ef53501f;
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          <LockUnauthorizedIcon size={20} />
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            row-gap: 8px;
          `}
        >
          <QSTypography
            css={css`
              font-size: 16px;
              font-weight: 600;
              line-height: 23.4px;
              letter-spacing: -0.27px;
              text-align: center;
              word-wrap: break-word;
              word-break: break-word;
              color: #2d2d2d;
            `}
          >
            {unauthorizedTitle ?? "You don't have the required permission"}
          </QSTypography>
          <QSTypography
            css={css`
              font-size: 14px;
              font-weight: 400;
              line-height: 21px;
              letter-spacing: -0.04px;
              text-align: center;
              color: #7a7a7a;
              word-wrap: break-word;
              word-break: break-word;
            `}
          >
            Please contact your org owner to share access
          </QSTypography>
        </div>
        {showOrgOwners && <OrgOwnersList featureName={featureName} />}
      </div>
    );
  }

  return (
    <div
      css={css`
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          font-family: 'Avenir Next';
          font-size: 16px;
          font-weight: 400;
          line-height: 1.3;
          text-align: center;
          color: ${TEXT_COLOR_LIGHT};
        `}
      >
        <QSTypography
          css={css`
            font-size: 74px;
            font-weight: 800;
          `}
        >
          OOPS!
        </QSTypography>
        <QSTypography
          css={css`
            font-size: 18px;
          `}
        >
          {error}
        </QSTypography>
      </div>
      <QSButton
        variant="text"
        onClick={() => window.location.reload()}
        css={css`
          background: none;
        `}
      >
        RELOAD PAGE
      </QSButton>
    </div>
  );
};

export default ErrorContainer;
